import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, errorText } from '../api';
import type { Tarif } from '../billtoro/types';
import { Dialog } from '../components/Dialog';
import { t, tl } from '../i18n';
import { fmtDate } from '../lib/format';
import { eurC } from '../lib/money';
import { useToast } from '../lib/toast';
import { isBilltoro } from '../produkt';
import { tarifFehler } from './tarifHinweis';
import type { PostEintrag } from './types';
import './post.css';

// Billtoro: keine Kanzlei, die druckt; Briefe kommen auf die nächste Rechnung von Billtoro (Preis aus tarife.json)
const BILLTORO = isBilltoro();

// „Per Post versenden“ (server/src/modules/rechnungstool/post): Die Rechnung geht als echter Brief an die Anschrift
// auf der Rechnung, über einen Druckdienst oder (ohne Druckdienst) druckt die Kanzlei sie aus und bringt sie zur Post.
// Ein kurzes Bestätigen: so steht es auf dem Umschlag, so viele Seiten, Preis. Kein Formular.

export interface PostBeleg {
  id: string;
  titel: string;
}

type Problem = 'storniert' | 'anschrift' | 'ausland' | 'fenster' | 'seiten';
interface Info {
  an: boolean;
  test?: boolean;
  // die Kanzlei druckt selbst
  kanzlei?: boolean;
  anschrift?: string[];
  seiten?: number;
  duplex?: boolean;
  preis?: number;
  problem?: { code: Problem; text: string } | null;
  kundeId?: string | null;
  verlauf: PostEintrag[];
}

const PROBLEM: Record<Problem, string> = {
  storniert: tl('Diese Rechnung ist storniert.'),
  anschrift: tl('Beim Kunden fehlt die vollständige Postanschrift (Straße, PLZ, Ort).'),
  ausland: tl('Per Post geht nur innerhalb Deutschlands.'),
  fenster: tl('Die Anschrift ist zu lang für das Fenster im Umschlag (höchstens 5 Zeilen).'),
  seiten: tl('Die Rechnung hat mehr als 12 Seiten. Das geht nicht als Brief.'),
};

// Eine Zeile im Verlauf: „Per Post · übergeben am …“, später „gedruckt und verschickt am …“. Druckt die Kanzlei
// selbst: „bei der Kanzlei seit …“, dann „verschickt am …“ (Datum, an dem die Kanzlei ihn als verschickt markiert hat).
export function postText(p: PostEintrag): string {
  if (p.kanzlei && !BILLTORO) {
    return p.status === 'verschickt'
      ? t('Per Post · verschickt am {datum}', { datum: fmtDate(p.verschicktAm ?? p.am) })
      : p.status === 'uebergeben'
        ? t('Per Post · bei der Kanzlei seit {datum}', { datum: fmtDate(p.uebergebenAm ?? p.am) })
        : p.status === 'neu'
          ? t('Per Post · wird übergeben')
          : p.status === 'storniert'
            ? t('Per Post · nicht verschickt, nicht berechnet')
            : t('Per Post · nicht übergeben, nicht berechnet');
  }
  const text =
    p.status === 'verschickt'
      ? t('Per Post · gedruckt und verschickt am {datum}', { datum: fmtDate(p.verschicktAm ?? p.am) })
      : p.status === 'uebergeben'
        ? t('Per Post · übergeben am {datum}', { datum: fmtDate(p.uebergebenAm ?? p.am) })
        : p.status === 'neu'
          ? t('Per Post · wird übergeben')
          : p.status === 'storniert'
            ? t('Per Post · storniert, nicht berechnet')
            : t('Per Post · nicht übergeben, nicht berechnet');
  return p.test ? `${text} ${t('(Testbetrieb)')}` : text;
}

export function PostDialog({
  beleg,
  tarif,
  onClose,
  onSent,
}: {
  beleg: PostBeleg | null;
  // Billtoro: Tarif der Firma (Preis je Brief)
  tarif?: Tarif | null;
  onClose(): void;
  onSent(): Promise<void> | void;
}) {
  const toast = useToast();
  const [info, setInfo] = useState<Info | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setInfo(null);
    setErr(null);
    if (!beleg) return;
    let aktuell = true;
    api
      .get<Info>(`/api/rechnungstool/rechnung/${beleg.id}/post`)
      .then((i) => aktuell && setInfo(i))
      .catch((e: unknown) => {
        if (!aktuell) return;
        // Tarif-Fehler (Billtoro): das Fenster „Mehr mit Billtoro“ übernimmt
        if (tarifFehler(e)) onClose();
        else setErr(errorText(e));
      });
    return () => {
      aktuell = false;
    };
  }, [beleg]);

  async function abschicken() {
    if (!beleg) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.post<{ kanzlei?: boolean }>(`/api/rechnungstool/rechnung/${beleg.id}/post`);
      toast(
        res.kanzlei && !BILLTORO
          ? t('Der Brief ist bei Ihrer Kanzlei. Sie druckt ihn aus und bringt ihn zur Post.')
          : t('Brief übergeben. Er wird gedruckt und verschickt.'),
      );
      await onSent();
      onClose();
    } catch (e) {
      if (tarifFehler(e)) onClose();
      else setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  const problem = info?.problem?.code ?? null;
  const kann = !!info?.an && !problem && !!info.anschrift?.length;
  return (
    <Dialog open={!!beleg} onClose={onClose} title={t('Per Post versenden')} className="pd-dlg">
      {beleg && <p className="pd-titel">{beleg.titel}</p>}
      {!info && !err && <p className="muted">{t('Einen Moment …')}</p>}
      {info && !info.an && <p className="pd-hint">{t('Der Briefversand ist nicht eingerichtet.')}</p>}
      {problem && (
        <p className="pd-hint">
          {t(PROBLEM[problem])}
          {(problem === 'anschrift' || problem === 'fenster') && info?.kundeId && (
            <>
              {' '}
              <Link to={`/rechnung/kunden/${info.kundeId}/bearbeiten`} onClick={onClose}>
                {t('Kunden bearbeiten')}
              </Link>
            </>
          )}
        </p>
      )}
      {kann && info && (
        <>
          <p className="pd-label">{t('So steht es auf dem Umschlag')}</p>
          <div className="pd-umschlag">
            <address className="pd-fenster" aria-label={t('Anschrift')}>
              {info.anschrift!.map((z, n) => (
                <span key={n}>{z}</span>
              ))}
            </address>
          </div>
          <ul className="pd-fakten">
            <li>
              <span>{t('Seiten')}</span>
              <b>
                {info.seiten}
                {info.duplex ? ` · ${t('beidseitig')}` : ''}
              </b>
            </li>
            <li className="pd-preis">
              {BILLTORO
                ? `${eurC((tarif?.briefPreisCent ?? Math.round((info.preis ?? 2.5) * 100)) / 100)} zzgl. USt je Brief, kommt auf Ihre nächste Billtoro-Rechnung.`
                : t('{preis} zzgl. USt, kommt auf Ihre Monatsrechnung', { preis: eurC(info.preis ?? 2.5) })}
            </li>
          </ul>
          {info.kanzlei && !BILLTORO && (
            <p className="pd-hint info">{t('Ihre Kanzlei druckt den Brief aus und bringt ihn zur Post, in der Regel am nächsten Werktag.')}</p>
          )}
          {info.test && !info.kanzlei && (
            <p className="pd-hint info">{t('Testbetrieb: Der Brief wird nicht gedruckt und nicht berechnet.')}</p>
          )}
        </>
      )}
      {info && info.verlauf.length > 0 && (
        <ul className="pd-verlauf" aria-label={t('Bisher per Post')}>
          {info.verlauf.slice(0, 5).map((p, n) => (
            <li key={n}>{postText(p)}</li>
          ))}
        </ul>
      )}
      {err && (
        <p className="pd-err" role="alert">
          {err}
        </p>
      )}
      <div className="dlg-actions pd-actions">
        <button type="button" className="btn" onClick={onClose}>
          {t('Abbrechen')}
        </button>
        {kann && (
          <button type="button" className="btn primary" disabled={busy} onClick={() => void abschicken()} autoFocus>
            {busy ? t('Wird übergeben …') : t('Abschicken')}
          </button>
        )}
      </div>
    </Dialog>
  );
}
